import * as React from "react";
import { useSearchParams } from "react-router";

import { ROBOQL_SEARCH_PARAM } from "@/shared/domain/query/roboqlSearchParam";
import type { ViewRecord } from "@/shared/domain/views";
import { useView } from "@/shared/domain/views/hooks";
import { VIEW_SEARCH_PARAM } from "@/shared/domain/views/viewSearchParam";

import { viewLinkErrorMessage } from "./viewLinkError";

export { VIEW_SEARCH_PARAM };

export interface ViewDeepLink {
  /** True while a `?view=` id is being resolved. */
  isResolving: boolean;
  /** A recipient-facing reason the link did not open, or `null`. */
  linkError: string | null;
}

/**
 * Absolute URL that reopens `view`. A plain function, so a Copy-link action
 * does not have to mount the resolver — which reaches for `ViewService` and
 * must stay behind the `release-views` gate.
 */
export function viewLinkFor(view: ViewRecord): string {
  const url = new URL(window.location.href);
  url.searchParams.set(VIEW_SEARCH_PARAM, view.view_id);
  return url.toString();
}

export interface UseViewDeepLinkOptions {
  /** The View currently loaded, so the URL can follow it. */
  loadedView: ViewRecord | null;
  /**
   * Called with the resolved View so the caller can apply and load it.
   *
   * Must report whether the apply took. Watching `loadedView` alone cannot
   * distinguish "not applied yet" from "never applied": an apply can stand
   * down — superseded, or abandoned because the customer moved the view — and
   * without the outcome this hook would hold the parameter open forever,
   * advertising a View that is not on screen.
   */
  onResolved: (view: ViewRecord) => Promise<boolean>;
  /**
   * A View the route loader resolved and `useViewFromLink` already built the
   * table from, so the in-binding has nothing left to do for it.
   *
   * Without this the hook would resolve the id a second time (from cache, but
   * still) and re-apply a View that is already on screen — which is not merely
   * wasted work: the apply path rewrites column order, and that emits a table
   * preferences save. Opening a link would silently write preferences.
   */
  seededViewId?: string;
}

/**
 * Two-way binding between `?view=<id>` and the loaded View.
 *
 * **In:** an id in the URL resolves once and is handed to `onResolved`. Only
 * once — re-resolving would reapply the View every time a filter was edited.
 *
 * **Out:** loading or clearing a View rewrites the param, so the link is
 * copyable at any moment. It `replace`s rather than pushes, so picking three
 * Views does not cost three presses of Back.
 *
 * Out is suppressed while In is resolving: on mount nothing is loaded, so an
 * unguarded Out would delete the id In is about to read.
 *
 * **In does nothing for a seeded View.** When the route loader resolved the id
 * before the list mounted, the table was built from it directly and there is
 * nothing to resolve or apply — see `seededViewId`.
 *
 * Call this from inside the flag-gated picker, never from the list — `useView`
 * reaches for `ViewService` whether or not an id is present.
 *
 * Known gap, now only for RoboQL: a View that has to be transpiled cannot be
 * seeded, so the list still mounts and fetches before it resolves and the
 * recipient sees an unfiltered table for one paint. Structured Views no longer
 * do this — `makeViewsLoader` resolves them in the route loader and
 * `useViewFromLink` builds the table from the record before the first fetch.
 * Closing the RoboQL half means transpiling in the loader too.
 */
export function useViewDeepLink({
  loadedView,
  onResolved,
  seededViewId,
}: UseViewDeepLinkOptions): ViewDeepLink {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get(VIEW_SEARCH_PARAM) ?? undefined;

  // One-shot per id: without this the cached record re-applies on every render
  // that touches the params.
  //
  // Pre-armed with a seeded id, which is the same statement one render earlier:
  // that View has already been resolved and applied, so treating it as handled
  // is exactly what the ref means. The out-binding then finds the URL already
  // correct and writes nothing.
  const resolvedRef = React.useRef<string | null>(seededViewId ?? null);
  // An id handed to `onResolved` whose apply has not concluded. Applying is
  // asynchronous, so `loadedView` lags the handover by at least a render and
  // the URL must not be rewritten in between.
  //
  // State rather than a ref, deliberately: releasing it has to re-run the sync
  // below. A ref release is invisible to React, which is the trap the give-up
  // branch already documents.
  const [awaitingApply, setAwaitingApply] = React.useState<string | null>(null);
  const pending =
    linkedId !== undefined && resolvedRef.current !== linkedId
      ? linkedId
      : undefined;

  const { data, error, isPending } = useView(pending);

  React.useEffect(() => {
    if (pending !== undefined && data !== undefined) {
      resolvedRef.current = pending;
      setAwaitingApply(pending);
      // Release when the apply concludes, however it concludes. On success the
      // caller has already set the loaded View by the time this runs, so the
      // sync below finds the URL already correct and writes nothing; on a stand
      // down it finds nothing loaded and strips the id.
      void onResolved(data).then(() => {
        setAwaitingApply((current) => (current === pending ? null : current));
      });
    }
  }, [data, onResolved, pending]);

  React.useEffect(() => {
    if (pending !== undefined && error !== null) {
      // Give up on this id so a failed link does not retry forever. Note this
      // does not re-render, so the out-binding below stays parked: a `?view=`
      // that cannot be resolved is left in the URL rather than stripped. That
      // suits a failed link — the id stays visible and a refresh retries it —
      // but it does mean the out-binding never runs for this navigation.
      resolvedRef.current = pending;
    }
  }, [error, pending]);

  const loadedId = loadedView?.view_id;
  const isResolving = pending !== undefined;
  // What the URL says right now, to compare against what would be written.
  const paramInUrl = searchParams.get(VIEW_SEARCH_PARAM);
  React.useEffect(() => {
    // Leave the URL alone while an id is resolving: the param is the input to
    // that resolution. A successful resolve re-renders through the caller's
    // state and hands control back; a failed one does not, and deliberately
    // leaves the id in the URL — see the give-up branch above.
    if (isResolving) {
      return;
    }

    // A resolved id whose apply has not concluded. `isResolving` above only
    // covers the fetch; the caller applies asynchronously, and for a RoboQL
    // View that is a whole server round trip. Stripping the id here would
    // delete the link being honoured, and the write-back when the apply lands
    // would be a second navigation — two route revalidations for a URL that
    // was already correct on arrival.
    if (paramInUrl !== null && paramInUrl === awaitingApply) {
      return;
    }

    // Nothing to change, so do not touch the router. `setSearchParams` is a
    // navigation, and a navigation revalidates the route — re-running every
    // matched loader. On a list page those loaders reconfigure Amplify, which
    // briefly makes the auth token unreadable, so an unnecessary write here
    // was enough to fail the page's own requests.
    if (paramInUrl === (loadedId ?? null)) {
      return;
    }

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        // A View replaces whatever filter a `?query=` link opened the table
        // with, so its param goes in the same write. `useRoboqlLinkSync` drops
        // it too, when the View's filters land, but this write builds on the
        // params of its own render, which may predate that drop and would
        // otherwise put `?query=` back.
        next.delete(ROBOQL_SEARCH_PARAM);
        if (loadedId === undefined) {
          next.delete(VIEW_SEARCH_PARAM);
        } else {
          next.set(VIEW_SEARCH_PARAM, loadedId);
        }
        return next;
      },
      { replace: true },
    );
  }, [awaitingApply, isResolving, loadedId, paramInUrl, setSearchParams]);

  return {
    isResolving: isResolving && isPending,
    linkError: error !== null ? viewLinkErrorMessage(error) : null,
  };
}
